import * as React from "react";

import { useIsMobile } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";

import { Portal } from "./portal";
import { Presence } from "./presence";

type OverlayVariant = "sheet" | "modal" | "responsive";

interface OverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: OverlayVariant;
  /** Portal container (defaults to document.body). */
  container?: Element | null;
  /** Whether clicking the backdrop closes the overlay. Default true. */
  dismissOnBackdrop?: boolean;
  /** Whether Escape closes the overlay. Default true. */
  dismissOnEscape?: boolean;
  backdropClassName?: string;
  className?: string;
  children: React.ReactNode;
  role?: "dialog" | "alertdialog";
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
}

function useBodyScrollLock(active: boolean) {
  React.useEffect(() => {
    if (!active || typeof document === "undefined") return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [active]);
}

const sheetClasses = cn(
  "fixed inset-x-0 bottom-0 z-50 bg-card rounded-t-2xl shadow-2xl",
  "max-h-[92dvh] overflow-y-auto overscroll-contain",
  "pb-[env(safe-area-inset-bottom)]",
  "data-open:animate-in data-open:slide-in-from-bottom data-open:duration-200",
  "data-closed:animate-out data-closed:slide-out-to-bottom data-closed:duration-150",
);

const modalClasses = cn(
  "fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
  "bg-card rounded-2xl shadow-2xl w-full max-w-md mx-4",
  "max-h-[85vh] overflow-y-auto",
);

/**
 * Responsive overlay: `sheet` on mobile, `modal` on desktop, or forced via `variant`.
 * Composes Portal + Presence + backdrop + body-scroll-lock.
 */
export function Overlay({
  open,
  onOpenChange,
  variant = "responsive",
  container,
  dismissOnBackdrop = true,
  dismissOnEscape = true,
  backdropClassName,
  className,
  children,
  role = "dialog",
  "aria-labelledby": ariaLabelledby,
  "aria-describedby": ariaDescribedby,
}: OverlayProps) {
  const isMobile = useIsMobile();
  const effective: "sheet" | "modal" =
    variant === "responsive" ? (isMobile ? "sheet" : "modal") : variant;
  const sheet = effective === "sheet";

  useBodyScrollLock(open);

  React.useEffect(() => {
    if (!open || !dismissOnEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dismissOnEscape, onOpenChange]);

  return (
    <Presence open={open}>
      {({ ref, dataProps }) => (
        <Portal container={container}>
          <div
            className={cn(
              "fixed inset-0 z-50 bg-black/60",
              "data-open:animate-in data-open:fade-in-0 data-open:duration-150",
              "data-closed:animate-out data-closed:fade-out-0 data-closed:duration-100",
              backdropClassName,
            )}
            {...dataProps}
            onClick={dismissOnBackdrop ? () => onOpenChange(false) : undefined}
          />
          <div
            ref={ref as React.RefCallback<HTMLDivElement>}
            role={role}
            aria-modal="true"
            aria-labelledby={ariaLabelledby}
            aria-describedby={ariaDescribedby}
            className={cn(sheet ? sheetClasses : modalClasses, className)}
            {...dataProps}
          >
            {sheet && (
              <div className="sticky top-0 flex justify-center pt-2 pb-1 pointer-events-none">
                <span className="h-1 w-10 rounded-full bg-muted-foreground/30" />
              </div>
            )}
            {children}
          </div>
        </Portal>
      )}
    </Presence>
  );
}
